import { queryOptions } from "@tanstack/react-query";
import { apiClient, orgHeader, unwrap } from "../../api/client";
import { allPages, type Device } from "../devices/queries";
import type { CustomAlarms } from "./customAlarms";

/** WEB-ALM-01: read again every minute and when the window gets focus. */
export const ALARMS_EVERY_MS = 60_000;

/** Every active and acknowledged alarm on the devices the person can view, all pages (WFX-02 AlarmItem). */
export const allAlarmsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/alarms", "all"],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/alarms", { params: { query: { limit: 500, cursor } } })),
      ),
    refetchInterval: ALARMS_EVERY_MS,
    refetchOnWindowFocus: true,
    enabled: !!orgId,
  });

/** The alarms that ended in the last 30 days, newest end first, all pages (alarm-history AH-01, vision-api 0.51.0). */
export const endedAlarmsQuery = (orgId: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/alarms", "ended"],
    queryFn: () =>
      allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/alarms", { params: { query: { state: "ended", limit: 500, cursor } } })),
      ),
    refetchInterval: ALARMS_EVERY_MS,
    refetchOnWindowFocus: true,
    enabled: !!orgId,
  });

/** Every device the person can view, read once for the alarm rows' names and spaces (not one request per device). */
export const alarmDevicesQuery = (orgId: string) =>
  queryOptions({
    // Not under "/v1/devices": the live patcher treats every query there as a paged device list.
    queryKey: ["org", orgId, "alarm-devices"],
    queryFn: async (): Promise<Map<string, Device>> => {
      const items = await allPages((cursor) =>
        unwrap(apiClient("customers").GET("/v1/devices", { params: { query: { limit: 500, cursor } } })),
      );
      return new Map(items.map((d) => [d.id, d]));
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

/**
 * Every custom alarm on the devices the person can view, all pages, with `allowed` and the devices for Add from the
 * first page (alarms-tab-custom-alarms CA-01, vision-api 0.75.0).
 */
export const orgAlarmRulesQuery = (orgId: string, language: string) =>
  queryOptions({
    queryKey: ["org", orgId, "/v1/alarm-rules", language],
    queryFn: async (): Promise<CustomAlarms> => {
      const out: CustomAlarms = { items: [], allowed: false, devices: [] };
      let cursor: string | undefined;
      let first = true;
      do {
        const page = await unwrap(
          apiClient("customers").GET("/v1/alarm-rules", {
            params: { query: { limit: 200, cursor }, header: { "Accept-Language": language, ...orgHeader(orgId) } },
          }),
        );
        out.items.push(...page.items);
        if (first) {
          out.allowed = page.allowed;
          out.devices = page.devices ?? [];
          first = false;
        }
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return out;
    },
    enabled: !!orgId,
  });
